import type { jsPDF as JsPdfDocument } from "jspdf";

export type MediaKitPdfPoint = { label: string; value: string };
export type MediaKitPdfContent = { title: string; views: number; engagement_rate: number; url?: string };

export type MediaKitPdfData = {
  name: string;
  channelHandle: string;
  email: string;
  bio: string;
  tags: string[];
  platform: "youtube" | "instagram";
  avatarUrl?: string;
  coverUrl?: string;
  shareUrl: string;
  subscribers: number | null;
  totalViews: number | null;
  avgMonthlyReach: number | null;
  engagementRate: number | null;
  ages: MediaKitPdfPoint[];
  geographies: MediaKitPdfPoint[];
  partnershipSummary: string;
  partnershipAction: string;
  topContentSubtitle: string;
  topContent: MediaKitPdfContent[];
  visibility: {
    subscribers: boolean;
    totals: boolean;
    engagement: boolean;
    demographics: boolean;
    partnership: boolean;
  };
};

type Color = readonly [number, number, number];

const C = {
  night: [13, 7, 25] as const,
  panel: [29, 18, 49] as const,
  panelStrong: [36, 21, 61] as const,
  border: [81, 60, 111] as const,
  white: [255, 255, 255] as const,
  text: [245, 241, 250] as const,
  muted: [184, 173, 204] as const,
  purple: [156, 70, 255] as const,
  pink: [247, 63, 166] as const,
  cyan: [70, 213, 229] as const,
  lavender: [210, 172, 255] as const,
} satisfies Record<string, Color>;

const safe = (value: string) => value
  .replace(/[\u2010-\u2015]/g, "-")
  .replace(/[\u2018\u2019]/g, "'")
  .replace(/[\u201c\u201d]/g, '"')
  .replace(/[^\x20-\x7E\n]/g, "");

const compact = (value: number | null) => value == null
  ? "--"
  : Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(Math.max(0, value));

const percent = (value: string) => Math.max(0, Math.min(100, Number.parseFloat(value) || 0));

const imageData = async (url?: string): Promise<string | null> => {
  if (!url) return null;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const loadImage = async (src: string): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = reject;
  image.src = src;
});

const roundedPath = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, radius: number) => {
  const r = Math.min(radius, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const roundImage = async (url?: string): Promise<string | null> => {
  const src = await imageData(url);
  if (!src) return null;
  try {
    const image = await loadImage(src);
    const canvas = document.createElement("canvas");
    canvas.width = 400;
    canvas.height = 400;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    const crop = Math.min(image.naturalWidth, image.naturalHeight);
    const sx = (image.naturalWidth - crop) / 2;
    const sy = (image.naturalHeight - crop) / 2;
    ctx.beginPath();
    ctx.arc(200, 200, 196, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(image, sx, sy, crop, crop, 0, 0, 400, 400);
    return canvas.toDataURL("image/png");
  } catch {
    return null;
  }
};

const coverImage = async (url?: string): Promise<string | null> => {
  const src = await imageData(url);
  if (!src) return null;
  try {
    const image = await loadImage(src);
    const width = 1400;
    const height = 480;
    const targetRatio = width / height;
    const sourceRatio = image.naturalWidth / image.naturalHeight;
    let sx = 0;
    let sy = 0;
    let sw = image.naturalWidth;
    let sh = image.naturalHeight;
    if (sourceRatio > targetRatio) {
      sw = image.naturalHeight * targetRatio;
      sx = (image.naturalWidth - sw) / 2;
    } else {
      sh = image.naturalWidth / targetRatio;
      sy = (image.naturalHeight - sh) / 2;
    }
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    roundedPath(ctx, 0, 0, width, height, 38);
    ctx.clip();
    ctx.drawImage(image, sx, sy, sw, sh, 0, 0, width, height);
    const shade = ctx.createLinearGradient(0, 0, 0, height);
    shade.addColorStop(0, "rgba(13,7,25,0.03)");
    shade.addColorStop(0.68, "rgba(13,7,25,0.14)");
    shade.addColorStop(1, "rgba(13,7,25,0.68)");
    ctx.fillStyle = shade;
    ctx.fillRect(0, 0, width, height);
    return canvas.toDataURL("image/jpeg", 0.94);
  } catch {
    return null;
  }
};

const pulseBackground = (variant: 1 | 2): string => {
  const canvas = document.createElement("canvas");
  canvas.width = 1240;
  canvas.height = 1754;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";

  const base = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
  base.addColorStop(0, variant === 1 ? "#150b27" : "#0d0719");
  base.addColorStop(0.55, "#0d0719");
  base.addColorStop(1, variant === 1 ? "#130920" : "#150b27");
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const glow = (x: number, y: number, radius: number, color: string) => {
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
    gradient.addColorStop(0, color);
    gradient.addColorStop(0.55, color.replace(/0\.([0-9]+)\)/, "0.12)"));
    gradient.addColorStop(1, "rgba(13,7,25,0)");
    ctx.fillStyle = gradient;
    ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
  };

  if (variant === 1) {
    glow(85, 215, 510, "rgba(136,45,246,0.38)");
    glow(1160, 500, 460, "rgba(244,42,159,0.25)");
    glow(210, 1550, 430, "rgba(45,202,224,0.16)");
  } else {
    glow(1110, 160, 510, "rgba(136,45,246,0.32)");
    glow(70, 1030, 480, "rgba(244,42,159,0.18)");
    glow(1120, 1570, 420, "rgba(45,202,224,0.16)");
  }

  ctx.lineWidth = 1;
  ctx.strokeStyle = "rgba(220,180,255,0.10)";
  [260, 390, 520].forEach((radius) => {
    ctx.beginPath();
    ctx.arc(variant === 1 ? 1150 : 90, variant === 1 ? 130 : 1620, radius, 0, Math.PI * 2);
    ctx.stroke();
  });
  return canvas.toDataURL("image/jpeg", 0.92);
};

const panel = (doc: JsPdfDocument, x: number, y: number, w: number, h: number, strong = false) => {
  const fill: Color = strong ? C.panelStrong : C.panel;
  doc.setFillColor(...fill);
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.28);
  doc.roundedRect(x, y, w, h, 4, 4, "FD");
};

const addPulseMark = (doc: JsPdfDocument, logo: string | null, x: number, y: number) => {
  if (logo) doc.addImage(logo, "PNG", x, y, 8.5, 8.5);
  else {
    doc.setFillColor(...C.purple);
    doc.circle(x + 4.25, y + 4.25, 4.25, "F");
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10.5);
  doc.setTextColor(...C.white);
  doc.text("PULSE", x + 11.5, y + 6.1);
};

const addPageFrame = (doc: JsPdfDocument, background: string, logo: string | null, page: number) => {
  doc.addImage(background, "JPEG", 0, 0, 210, 297);
  addPulseMark(doc, logo, 16, 13);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(6.5);
  doc.setTextColor(...C.muted);
  doc.text("CREATOR INTELLIGENCE", 194, 18.5, { align: "right" });
  doc.setDrawColor(88, 65, 119);
  doc.setLineWidth(0.25);
  doc.line(16, 282.5, 194, 282.5);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(5.8);
  doc.text("LIVE CHANNEL EVIDENCE  /  PULSE", 16, 288.2);
  doc.text(`${String(page).padStart(2, "0")} / 02`, 194, 288.2, { align: "right" });
};

const metricCard = (
  doc: JsPdfDocument,
  x: number,
  y: number,
  w: number,
  label: string,
  value: string,
  note: string,
  accent: Color,
) => {
  panel(doc, x, y, w, 29);
  doc.setFillColor(...accent);
  doc.roundedRect(x + 5, y + 5, 9, 1.4, 0.7, 0.7, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(6.3);
  doc.setTextColor(...C.muted);
  doc.text(safe(label).toUpperCase(), x + 5, y + 11.5);
  doc.setFontSize(16);
  doc.setTextColor(...C.white);
  doc.text(value, x + 5, y + 21);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(5.5);
  doc.setTextColor(...C.muted);
  doc.text(safe(note), x + w - 5, y + 21, { align: "right", maxWidth: w * 0.48 });
};

export async function generateMediaKitPdf(data: MediaKitPdfData): Promise<Blob> {
  const [{ jsPDF }, qrCodeModule] = await Promise.all([import("jspdf"), import("qrcode")]);
  const QRCode = qrCodeModule.default;
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait", compress: true });
  const [logo, cover, avatar, qr] = await Promise.all([
    imageData(`${window.location.origin}/pulse-icon.png`),
    coverImage(data.coverUrl),
    roundImage(data.avatarUrl),
    QRCode.toDataURL(data.shareUrl, {
      width: 768,
      margin: 1,
      errorCorrectionLevel: "H",
      color: { dark: "#150d24", light: "#FFFFFF" },
    }),
  ]);
  const backgroundOne = pulseBackground(1);
  const backgroundTwo = pulseBackground(2);
  const generatedDate = new Date().toLocaleDateString("en", { year: "numeric", month: "long", day: "numeric" }).toUpperCase();

  addPageFrame(doc, backgroundOne, logo, 1);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(6.4);
  doc.setTextColor(...C.lavender);
  doc.text("OFFICIAL CREATOR MEDIA KIT", 16, 31);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...C.muted);
  doc.text(`GENERATED ${generatedDate}`, 194, 31, { align: "right" });

  if (cover) {
    doc.addImage(cover, "JPEG", 16, 37, 178, 61);
    doc.setDrawColor(116, 80, 154);
    doc.setLineWidth(0.35);
    doc.roundedRect(16, 37, 178, 61, 4.5, 4.5, "S");
  } else {
    panel(doc, 16, 37, 178, 61, true);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(...C.muted);
    doc.text("CONNECTED CHANNEL", 105, 68, { align: "center" });
  }

  panel(doc, 22, 87, 166, 61, true);
  if (avatar) {
    doc.setFillColor(...C.white);
    doc.circle(39, 104, 13.2, "F");
    doc.addImage(avatar, "PNG", 26.5, 91.5, 25, 25);
  } else {
    doc.setFillColor(...C.purple);
    doc.circle(39, 104, 12.5, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(...C.white);
    doc.text(safe(data.name).slice(0, 2).toUpperCase(), 39, 107, { align: "center" });
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...C.white);
  doc.text(doc.splitTextToSize(safe(data.name), 91)[0], 57, 100);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.4);
  doc.setTextColor(...C.lavender);
  doc.text([safe(data.channelHandle), safe(data.email)].filter(Boolean).join("  /  "), 57, 107, { maxWidth: 94 });

  let chipX = 57;
  data.tags.slice(0, 4).forEach((tag) => {
    const label = safe(tag).toUpperCase();
    const width = Math.min(31, Math.max(14, doc.getTextWidth(label) + 6));
    if (chipX + width <= 151) {
      doc.setFillColor(62, 35, 91);
      doc.setDrawColor(104, 66, 145);
      doc.roundedRect(chipX, 112, width, 6.4, 3.2, 3.2, "FD");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(5.3);
      doc.setTextColor(...C.text);
      doc.text(label, chipX + width / 2, 116.2, { align: "center" });
      chipX += width + 2;
    }
  });

  doc.setFillColor(...C.white);
  doc.roundedRect(158, 94, 22, 22, 2.5, 2.5, "F");
  doc.addImage(qr, "PNG", 160, 96, 18, 18);
  doc.link(158, 94, 22, 22, { url: data.shareUrl });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(5.4);
  doc.setTextColor(...C.white);
  doc.text("SCAN LIVE KIT", 169, 121, { align: "center" });

  if (data.bio.trim()) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.8);
    doc.setTextColor(...C.text);
    const bioLines = doc.splitTextToSize(safe(data.bio.trim()), 148).slice(0, 3);
    doc.text(bioLines, 30, 130, { lineHeightFactor: 1.42 });
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(6.3);
  doc.setTextColor(data.platform === "youtube" ? 255 : C.pink[0], data.platform === "youtube" ? 89 : C.pink[1], data.platform === "youtube" ? 89 : C.pink[2]);
  doc.text(`${data.platform.toUpperCase()}  /  ${data.platform === "youtube" ? "LONG-FORM" : "CONNECTED AUDIENCE"}`, 16, 161);
  doc.setFontSize(20);
  doc.setTextColor(...C.white);
  doc.text("Audience, distilled.", 16, 173);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.2);
  doc.setTextColor(...C.muted);
  doc.text("A clear snapshot of the connected channel's current performance.", 16, 180);

  const visibleMetrics = [
    data.visibility.subscribers ? [data.platform === "instagram" ? "Followers" : "Subscribers", compact(data.subscribers), "Public total", C.purple] as const : null,
    data.visibility.totals ? ["Total views", compact(data.totalViews), "Public total", C.pink] as const : null,
    data.visibility.totals ? ["Monthly reach", compact(data.avgMonthlyReach), "Connected data", C.cyan] as const : null,
    data.visibility.engagement ? ["Engagement", data.engagementRate == null ? "--" : `${data.engagementRate.toFixed(1)}%`, data.engagementRate == null ? "Data pending" : "Measured rate", C.lavender] as const : null,
  ].filter(Boolean) as Array<readonly [string, string, string, Color]>;
  const metricWidth = visibleMetrics.length ? (178 - (visibleMetrics.length - 1) * 4) / visibleMetrics.length : 178;
  visibleMetrics.forEach((item, index) => metricCard(doc, 16 + index * (metricWidth + 4), 188, metricWidth, ...item));

  panel(doc, 16, 226, 178, 42, true);
  doc.setFillColor(...C.purple);
  doc.circle(27, 239, 5, "F");
  doc.setFillColor(...C.pink);
  doc.circle(29.5, 236.5, 2.2, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(6.2);
  doc.setTextColor(...C.lavender);
  doc.text("CONNECTED BY PULSE", 39, 236);
  doc.setFontSize(13);
  doc.setTextColor(...C.white);
  doc.text("One channel. One story. Always current.", 39, 245);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.8);
  doc.setTextColor(...C.muted);
  doc.text("Scan the live kit for the latest public profile, content, and performance signals.", 39, 253);
  doc.setDrawColor(...C.border);
  doc.line(156, 234, 156, 260);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.setTextColor(...C.text);
  doc.text("LIVE MEDIA KIT", 173, 240, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(5.7);
  doc.setTextColor(...C.muted);
  doc.text("pulse.me", 173, 248, { align: "center" });

  doc.addPage();
  addPageFrame(doc, backgroundTwo, logo, 2);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(6.3);
  doc.setTextColor(...C.lavender);
  doc.text("AUDIENCE INTELLIGENCE", 16, 35);
  doc.setFontSize(22);
  doc.setTextColor(...C.white);
  doc.text("The signal behind the audience.", 16, 48);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...C.muted);
  doc.text("Sponsor-ready context for audience composition, partnership fit, and content performance.", 16, 56);

  let y = 68;
  if (data.visibility.demographics) {
    const groups: Array<[string, MediaKitPdfPoint[], string, Color]> = [
      ["Age distribution", data.ages.slice(0, 5), "Age data pending", C.purple],
      ["Geographic reach", data.geographies.slice(0, 5), "Geography data pending", C.cyan],
    ];
    groups.forEach(([title, rows, empty, accent], groupIndex) => {
      const x = groupIndex ? 107 : 16;
      panel(doc, x, y, 87, 61);
      doc.setFillColor(...accent);
      doc.roundedRect(x + 7, y + 7, 10, 1.5, 0.75, 0.75, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...C.white);
      doc.text(title, x + 7, y + 17);
      if (!rows.length) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.2);
        doc.setTextColor(...C.muted);
        doc.text(empty, x + 43.5, y + 40, { align: "center" });
      } else {
        rows.forEach((row, index) => {
          const rowY = y + 28 + index * 6.5;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(6.2);
          doc.setTextColor(...C.muted);
          doc.text(safe(row.label), x + 7, rowY);
          doc.setFillColor(57, 42, 77);
          doc.roundedRect(x + 29, rowY - 2.6, 38, 1.8, 0.9, 0.9, "F");
          doc.setFillColor(...accent);
          doc.roundedRect(x + 29, rowY - 2.6, 38 * percent(row.value) / 100, 1.8, 0.9, 0.9, "F");
          doc.setFont("helvetica", "bold");
          doc.setTextColor(...C.text);
          doc.text(safe(row.value), x + 79, rowY, { align: "right" });
        });
      }
    });
    y += 69;
  }

  if (data.visibility.partnership) {
    panel(doc, 16, y, 178, 47, true);
    doc.setFillColor(...C.purple);
    doc.roundedRect(16, y, 3.5, 47, 1.7, 0, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6.2);
    doc.setTextColor(...C.lavender);
    doc.text("IRIS PARTNERSHIP FIT", 27, y + 10);
    doc.setFontSize(14);
    doc.setTextColor(...C.white);
    doc.text("Built for the right collaboration.", 27, y + 20);
    const fit = safe([data.partnershipSummary, data.partnershipAction].filter(Boolean).join(" "));
    if (fit) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...C.muted);
      doc.text(doc.splitTextToSize(fit, 157).slice(0, 3), 27, y + 29, { lineHeightFactor: 1.42 });
    }
    y += 55;
  }

  // Leave an unmistakable visual break before the closing CTA. A 2 mm gap
  // can disappear once thin PDF borders are rasterized by some viewers.
  panel(doc, 16, y, 178, 57);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...C.white);
  doc.text("Top performing content", 23, y + 13);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.5);
  doc.setTextColor(...C.muted);
  doc.text(safe(data.topContentSubtitle), 23, y + 21, { maxWidth: 158 });
  if (!data.topContent.length) {
    doc.setFillColor(23, 14, 39);
    doc.setDrawColor(...C.border);
    doc.roundedRect(23, y + 29, 164, 22, 3, 3, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(...C.muted);
    doc.text("No qualifying videos in this period", 105, y + 42, { align: "center" });
  } else {
    data.topContent.slice(0, 3).forEach((item, index) => {
      const itemY = y + 31 + index * 9;
      const accent: Color = index === 0 ? C.purple : index === 1 ? C.pink : C.cyan;
      doc.setFillColor(...accent);
      doc.circle(27, itemY - 1, 1.7, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(6.7);
      doc.setTextColor(...C.text);
      doc.text(doc.splitTextToSize(safe(item.title), 104)[0], 32, itemY);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(6);
      doc.setTextColor(...C.muted);
      doc.text(`${compact(item.views)} views  /  ${item.engagement_rate.toFixed(1)}% engagement`, 187, itemY, { align: "right" });
      if (item.url) doc.link(32, itemY - 4, 104, 7, { url: item.url });
    });
  }

  const ctaY = 255;
  panel(doc, 16, ctaY, 178, 24, true);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...C.white);
  doc.text("Keep the conversation moving.", 24, ctaY + 9.5);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.2);
  doc.setTextColor(...C.muted);
  doc.text("Scan for the live channel profile and current contact details.", 24, ctaY + 16.5);
  doc.setFillColor(...C.white);
  doc.roundedRect(169, ctaY + 3, 18, 18, 2, 2, "F");
  doc.addImage(qr, "PNG", 170.5, ctaY + 4.5, 15, 15);
  doc.link(169, ctaY + 3, 18, 18, { url: data.shareUrl });

  doc.setProperties({
    title: `${safe(data.name)} - Pulse Media Kit`,
    subject: "Creator Media Kit",
    author: safe(data.name),
    creator: "Pulse",
  });
  const blob = doc.output("blob");
  const filename = `${safe(data.name).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "creator"}-pulse-media-kit.pdf`;
  doc.save(filename);
  return blob;
}
